// src/pages/dev/FormGalleryPage.tsx
// Living reference for the desktop-app form idiom (docs/ux/14-forms.md).
// Dev-only page: strings are intentionally not localized.

import { useEffect, useRef, useState } from 'react';
import { Button } from '@react/components';
import {
  AutoGrowTextarea,
  FormDialog,
  FormRow,
  InlineEditText,
  SegmentedControl,
  TitleInput,
} from '@/components/form';
import { PrioritySelect } from '@/components/PrioritySelect';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';

const PROJECTS = [
  { value: 'nord', label: 'Nord — launch' },
  { value: 'atlas', label: 'Atlas — billing' },
];

interface DemoItem {
  key: string;
  title: string;
  priority: string;
}

function SectionHeader({ title, note }: { title: string; note: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{note}</p>
    </div>
  );
}

/** Flashes "Saved · <what>" for a moment after each per-field commit. */
function useSavedFlash(): [string | null, (what: string) => void] {
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(null), 2000);
    return () => clearTimeout(timer);
  }, [saved]);
  return [saved, setSaved];
}

export function FormGalleryPage() {
  // --- create dialog demo ---
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [moreFields, setMoreFields] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('TASK_PRIORITY_MEDIUM');
  const [project, setProject] = useState('nord');
  const [estimate, setEstimate] = useState('');
  const [created, setCreated] = useState<DemoItem[]>([]);
  const seqRef = useRef(101);
  const descRef = useRef<HTMLTextAreaElement>(null);

  const dirty = !!(title.trim() || description.trim() || estimate);

  const resetDraft = () => {
    setTitle('');
    setDescription('');
    setPriority('TASK_PRIORITY_MEDIUM');
    setEstimate('');
    setMoreFields(false);
  };

  const submit = () => {
    if (!title.trim() || isSubmitting) return;
    setIsSubmitting(true);
    // Simulated request so the button's loading state is visible.
    setTimeout(() => {
      const key = `DEMO-${seqRef.current++}`;
      setCreated((prev) => [{ key, title: title.trim(), priority }, ...prev]);
      setIsSubmitting(false);
      setDialogOpen(false);
      resetDraft();
    }, 500);
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (dialogOpen) return;
      if (e.key === 'c' || e.key === 'C' || e.key === 'с' || e.key === 'С') {
        e.preventDefault();
        setDialogOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [dialogOpen]);

  // --- inline edit demo ---
  const [itemTitle, setItemTitle] = useState('Empty states for the deals list');
  const [itemDescription, setItemDescription] = useState('');
  const [itemPriority, setItemPriority] = useState('TASK_PRIORITY_HIGH');
  const [itemStage, setItemStage] = useState('active');
  const [inlineSaved, flashInlineSaved] = useSavedFlash();

  // --- settings demo ---
  const [wsName, setWsName] = useState('Nord — launch');
  const [density, setDensity] = useState('comfortable');
  const [notify, setNotify] = useState(true);
  const [wipLimit, setWipLimit] = useState('5');
  const [settingsSaved, flashSettingsSaved] = useSavedFlash();

  return (
    <div className="mx-auto max-w-4xl space-y-12">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Forms — desktop-app idiom</h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Living reference for <span className="font-mono text-xs">docs/ux/14-forms.md</span>.
          Three surfaces replace web forms: a create dialog, inline editing on detail surfaces,
          and label-left settings rows. Everything below is interactive.
        </p>
      </div>

      {/* ---------- 1. Create dialog ---------- */}
      <section>
        <SectionHeader
          title="Create dialog"
          note="Opens over the current page — no /new route. Ghost title and description (the dialog is the box), compact property controls, rare fields behind “more”. ⌘⏎ submits, Esc with a dirty draft asks twice."
        />
        <div className="flex items-center gap-3">
          <Button onClick={() => setDialogOpen(true)}>
            + New item
            <span className="ml-2 border-l border-primary-foreground/30 pl-2 font-mono text-[10.5px] opacity-75">
              C
            </span>
          </Button>
          <span className="text-xs text-muted-foreground">or press C anywhere on this page</span>
        </div>

        {created.length > 0 && (
          <div className="mt-4 divide-y divide-border rounded-lg border border-border">
            {created.map((item) => (
              <div key={item.key} className="flex items-center gap-3 px-4 py-2 text-sm">
                <span className="font-mono text-xs text-muted-foreground">{item.key}</span>
                <span className="flex-1 truncate">{item.title}</span>
                <PrioritySelect value={item.priority} onSelect={() => undefined} disabled />
              </div>
            ))}
          </div>
        )}

        <FormDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            resetDraft();
          }}
          onSubmit={submit}
          submitLabel="Create item"
          canSubmit={!!title.trim()}
          isSubmitting={isSubmitting}
          dirty={dirty}
          aria-label="New item"
          context={
            <Select
              value={project}
              onChange={(e) => setProject(e.target.value)}
              options={PROJECTS}
              fullWidth={false}
              className="w-44"
              aria-label="Project"
            />
          }
          footerLeft={
            <button
              type="button"
              onClick={() => setMoreFields((v) => !v)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {moreFields ? '− fewer fields' : '+ more fields'}
            </button>
          }
        >
          <div className="px-4 pb-1 pt-2">
            <TitleInput
              value={title}
              onChange={setTitle}
              placeholder="Item title…"
              initialFocus
              onEnter={() => descRef.current?.focus()}
            />
            <AutoGrowTextarea
              ref={descRef}
              value={description}
              onChange={setDescription}
              placeholder="Add description…"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 px-4 pb-3 pt-1">
            <PrioritySelect value={priority} onSelect={setPriority} />
          </div>
          {moreFields && (
            <div className="border-t border-border px-4 py-1">
              <FormRow label="Estimate, h" labelWidth="sm" htmlFor="gallery-estimate">
                <span className="inline-flex items-center gap-2">
                  <input
                    id="gallery-estimate"
                    type="number"
                    value={estimate}
                    onChange={(e) => setEstimate(e.target.value)}
                    placeholder="—"
                    className="w-24 rounded-md border border-input bg-background px-3 py-1.5 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  {estimate && (
                    <button
                      type="button"
                      onClick={() => setEstimate('')}
                      aria-label="Clear estimate"
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      ✕
                    </button>
                  )}
                </span>
              </FormRow>
            </div>
          )}
        </FormDialog>
      </section>

      {/* ---------- 2. Inline edit ---------- */}
      <section>
        <SectionHeader
          title="Inline edit — the only edit surface"
          note="No edit mode, no /edit route, no Save button. Click the title or a property value, change it, and it commits immediately (Enter/blur saves, Esc reverts)."
        />
        <div className="max-w-md rounded-xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">DEMO-42</span>
            <span className="flex-1" />
            <span
              role="status"
              className={`text-xs text-muted-foreground transition-opacity ${inlineSaved ? 'opacity-100' : 'opacity-0'}`}
            >
              Saved · {inlineSaved}
            </span>
          </div>
          <InlineEditText
            value={itemTitle}
            onCommit={(next) => {
              setItemTitle(next);
              flashInlineSaved('title');
            }}
            variant="title"
            placeholder="Item title…"
            aria-label="Item title"
          />
          <div className="mt-3">
            <FormRow label="Priority" labelWidth="sm">
              <PrioritySelect
                value={itemPriority}
                onSelect={(p) => {
                  setItemPriority(p);
                  flashInlineSaved('priority');
                }}
              />
            </FormRow>
            <FormRow label="Stage" labelWidth="sm">
              <SegmentedControl
                aria-label="Stage"
                size="sm"
                value={itemStage}
                onChange={(v) => {
                  setItemStage(v);
                  flashInlineSaved('stage');
                }}
                options={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'active', label: 'Active' },
                  { value: 'done', label: 'Done' },
                ]}
              />
            </FormRow>
          </div>
          <div className="mt-2">
            <InlineEditText
              value={itemDescription}
              onCommit={(next) => {
                setItemDescription(next);
                flashInlineSaved('description');
              }}
              allowEmpty
              placeholder="Add description…"
              aria-label="Item description"
            />
          </div>
        </div>
      </section>

      {/* ---------- 3. Settings rows ---------- */}
      <section>
        <SectionHeader
          title="Settings rows"
          note="Label left, content-sized control right. Toggles and switches save on change; text saves on Enter/blur. No form-wide Save button."
        />
        <div className="max-w-xl rounded-xl border border-border bg-card px-5 py-2">
          <div className="flex justify-end pt-2">
            <span
              role="status"
              className={`text-xs text-muted-foreground transition-opacity ${settingsSaved ? 'opacity-100' : 'opacity-0'}`}
            >
              Saved · {settingsSaved}
            </span>
          </div>
          <FormRow label="Name" hint="Visible to the whole team" htmlFor="gallery-ws-name">
            <input
              id="gallery-ws-name"
              value={wsName}
              onChange={(e) => setWsName(e.target.value)}
              onBlur={() => wsName.trim() && flashSettingsSaved('name')}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
              autoCapitalize="off"
              className="w-56 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label="Density" hint="Compact for power users">
            <SegmentedControl
              aria-label="Density"
              value={density}
              onChange={(v) => {
                setDensity(v);
                flashSettingsSaved('density');
              }}
              options={[
                { value: 'compact', label: 'Compact' },
                { value: 'comfortable', label: 'Comfortable' },
                { value: 'cozy', label: 'Cozy' },
              ]}
            />
          </FormRow>
          <FormRow label="Notifications">
            <ToggleSwitch
              label="Email me on mentions"
              checked={notify}
              onChange={(checked) => {
                setNotify(checked);
                flashSettingsSaved('notifications');
              }}
            />
          </FormRow>
          <FormRow label="WIP limit" hint="Empty means no limit" htmlFor="gallery-wip">
            <span className="inline-flex items-center gap-2">
              <input
                id="gallery-wip"
                type="number"
                value={wipLimit}
                onChange={(e) => setWipLimit(e.target.value)}
                onBlur={() => flashSettingsSaved('WIP limit')}
                placeholder="—"
                className="w-24 rounded-md border border-input bg-background px-3 py-1.5 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {wipLimit && (
                <button
                  type="button"
                  onClick={() => {
                    setWipLimit('');
                    flashSettingsSaved('WIP limit');
                  }}
                  aria-label="Clear WIP limit"
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  ✕
                </button>
              )}
            </span>
          </FormRow>
        </div>
      </section>

      {/* ---------- 4. Keyboard ---------- */}
      <section>
        <SectionHeader title="Keyboard" note="Every form surface supports the same keys." />
        <table className="text-sm">
          <tbody>
            {[
              ['C', 'open the create dialog in context'],
              ['⌘⏎ / Ctrl⏎', 'submit from anywhere in the form'],
              ['Esc', 'close; double-press to discard a dirty draft; revert inline edit'],
              ['Enter', 'title → jump to description; inline edit → commit'],
              ['Tab', 'focus order = visual order'],
            ].map(([key, action]) => (
              <tr key={key}>
                <td className="py-1 pr-6">
                  <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                    {key}
                  </kbd>
                </td>
                <td className="py-1 text-muted-foreground">{action}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

export default FormGalleryPage;
